import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, HeartHandshakeIcon, PencilIcon, PlusIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  ListField,
  LoadingState,
  PageEyebrow,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  TextArea,
  TextInput,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { readMyIdentity } from '@/lib/events';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { cn } from '@/lib/utils';
import {
  CHARITY_STATUS,
  CHARITY_STATUS_LABELS,
  canSyncCharities,
  charityStatusTone,
  charitySyncSummaryText,
  createCharity,
  deleteCharity,
  listCharities,
  readCharity,
  regionsText,
  syncCharities,
  updateCharity,
  type Charity,
  type CharityContact,
  type CharityStatus,
  type CharityWrite,
} from '@/lib/charities';
import { CharityEventHistory } from './charity-events';

const CHARITY_SORT_FIELDS = { name: 'name', status: 'status', regions: 'regionsLabel', events: 'eventCount', updated: 'updatedMs' } as const;
const CHARITY_SORT_KEYS = Object.values(CHARITY_SORT_FIELDS);
const DEFAULT_CHARITY_SORT: DataTableSort = { key: CHARITY_SORT_FIELDS.name, direction: 'asc' };

type StatusFilter = 'all' | CharityStatus;

interface CharityRow extends Charity {
  readonly regionsLabel: string;
  readonly contactsText: string;
  readonly eventCount: number;
  readonly updatedMs: number | null;
}

function charityRow(charity: Charity): CharityRow {
  const updatedMs = charity.updatedAt ? Date.parse(charity.updatedAt) : NaN;
  return {
    ...charity,
    regionsLabel: regionsText(charity),
    contactsText: charity.contacts.map((contact) => contact.name ?? '').join(', '),
    eventCount: charity.history.length,
    updatedMs: Number.isNaN(updatedMs) ? null : updatedMs,
  };
}

/**
 * The charity directory: who BCE has worked with or vetted, where they
 * operate, and whether they are approved. Internal roles only.
 *
 * @returns The charities list page.
 */
export function CharitiesPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [removing, setRemoving] = useState<Charity | null>(null);
  const { search, setSearch } = useUrlSearch();
  const { sort, onSort } = useTableSort(CHARITY_SORT_KEYS, DEFAULT_CHARITY_SORT);

  const charities = useQuery({ queryKey: ['charities'], queryFn: ({ signal }) => listCharities({}, signal) });
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const canSync = canSyncCharities(identity.data?.role);
  const sync = useMutation({
    mutationFn: syncCharities,
    onSuccess: async (summary) => {
      toast.success(`Synced from Salesforce: ${charitySyncSummaryText(summary)}.`);
      await queryClient.invalidateQueries({ queryKey: ['charities'] });
    },
    onError: (error) => {
      toast.error(`Salesforce sync failed: ${errorMessage(error)}`);
    },
  });
  const remove = useMutation({
    mutationFn: (charity: Charity) => deleteCharity(charity._id),
    onSuccess: async (_result, charity) => {
      toast.success(`Removed ${charity.name}`);
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: ['charities'] });
    },
  });

  const rows = charities.data
    ? sortRows(
        filterRows(charities.data.map(charityRow), search, ['name', 'regionsLabel', 'contactsText']).filter((row) => status === 'all' || row.status === status),
        sort,
      )
    : [];
  const counts = { all: charities.data?.length ?? 0, approved: 0, pending: 0, do_not_use: 0 };
  for (const charity of charities.data ?? []) counts[charity.status] += 1;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="PEOPLE"
        title="Charities"
        subtitle="Every charity BCE has built for or vetted: where they operate, who to call, and whether they are approved for the next event. Charities from Salesforce sync in every six hours."
        action={
          <div className="flex flex-wrap items-center gap-2">
            {canSync ? (
              <span data-guide="charities-sync">
                <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
                  <RefreshCwIcon aria-hidden className={cn('size-4', sync.isPending && 'animate-spin')} /> {sync.isPending ? 'Syncing…' : 'Sync from Salesforce'}
                </Button>
              </span>
            ) : null}
            <span data-guide="charities-new">
              <Button onClick={() => navigate('/charities/new')}>
                <PlusIcon aria-hidden className="size-4" /> New charity
              </Button>
            </span>
          </div>
        }
      />
      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div data-guide="charities-filters" className="-mx-1 flex flex-wrap gap-1">
            {(['all', CHARITY_STATUS.approved, CHARITY_STATUS.pending, CHARITY_STATUS.doNotUse] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatus(key)}
                className={cn(
                  'rounded-portal-pill px-2.5 py-1 text-portal-micro font-bold transition-colors',
                  status === key ? 'bg-brand text-white' : 'text-portal-muted hover:bg-portal-bg',
                )}
              >
                {key === 'all' ? 'All' : CHARITY_STATUS_LABELS[key]} · {counts[key]}
              </button>
            ))}
          </div>
          <SearchInput data-guide="charities-search" label="Search charities" placeholder="Name, city or contact" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full max-w-sm" />
        </div>

        {charities.isPending ? (
          <LoadingState label="Loading charities…" />
        ) : charities.isError ? (
          <ErrorNotice title="Charities unavailable" message={errorMessage(charities.error)} />
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<HeartHandshakeIcon aria-hidden className="size-6" />}
              title={charities.data.length === 0 ? 'No charities yet' : 'No charities match'}
              message={charities.data.length === 0 ? canSync ? 'Add the first one, or sync the charities Salesforce already knows.' : 'Add the first one, or ask an admin to sync the charities Salesforce already knows.' : 'Try another name, city or status.'}
              action={<Button onClick={() => navigate('/charities/new')}>New charity</Button>}
            />
          </Panel>
        ) : (
          <div data-guide="charities-table"><DataTable
            headers={[
              { label: 'Charity', sortKey: CHARITY_SORT_FIELDS.name },
              { label: 'Status', sortKey: CHARITY_SORT_FIELDS.status },
              { label: 'Where', sortKey: CHARITY_SORT_FIELDS.regions },
              'Contacts',
              { label: 'Events', sortKey: CHARITY_SORT_FIELDS.events },
              { label: 'Updated', sortKey: CHARITY_SORT_FIELDS.updated },
              'Actions',
            ]}
            visuallyHiddenHeaders={[6]}
            rightAlignedHeaders={[4]}
            sort={sort}
            onSort={onSort}
            minWidth={900}
          >
            {rows.map((charity) => (
              <tr key={charity._id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button type="button" onClick={() => navigate(`/charities/${charity._id}`)} className="block cursor-pointer text-left font-bold underline-offset-2 hover:underline">
                    {charity.name}
                  </button>
                  {charity.causeTags.length ? <span className="block text-portal-micro text-portal-muted">{charity.causeTags.join(', ')}</span> : null}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  <Pill tone={charityStatusTone(charity.status)}>{CHARITY_STATUS_LABELS[charity.status]}</Pill>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{charity.regionsLabel || '—'}</td>
                <td className="p-3 text-portal-small text-portal-muted">
                  <span className="block max-w-[240px] truncate">{charity.contactsText || '—'}</span>
                </td>
                <td className="p-3 text-right text-portal-small text-portal-muted tabular-nums">{charity.eventCount}</td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                  {charity.updatedMs !== null ? new Date(charity.updatedMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </td>
                <td className="p-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" data-guide="charities-row-menu" aria-label={`Actions for ${charity.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                        <EllipsisIcon aria-hidden className="size-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => navigate(`/charities/${charity._id}`)}>
                        <PencilIcon aria-hidden className="size-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(charity)}>
                        <XIcon aria-hidden className="size-4" /> Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </DataTable></div>
        )}
      </div>
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing?.name ?? 'this charity'}?`}
        message="It leaves the directory; events that used it keep their plan."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </section>
  );
}

/** The create/edit page for one charity — a page, not a modal. */
export function CharityDetailPage(): React.ReactElement {
  const { charityId } = useParams<{ charityId: string }>();
  const isNew = charityId === undefined || charityId === 'new';
  const charity = useQuery({ queryKey: ['charity', charityId], queryFn: ({ signal }) => readCharity(charityId ?? '', signal), enabled: !isNew });

  if (!isNew && charity.isPending) return <LoadingState label="Loading charity…" />;
  if (!isNew && charity.isError) return <ErrorNotice title="Charity unavailable" message={errorMessage(charity.error)} />;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/charities">All charities</BackLink>
      <header className="mt-4">
        <PageEyebrow>CHARITY</PageEyebrow>
        <h1 className="text-portal-page tracking-[-1.7px]">{charity.data?.name ?? 'New charity'}</h1>
        {charity.data ? <SalesforceSummary charity={charity.data} /> : null}
      </header>
      <CharityForm key={charity.data?._id ?? 'new'} charity={charity.data ?? null} />
      {charity.data ? (
        <div className="mt-8">
          <CharityEventHistory charityId={charity.data._id} charityName={charity.data.name} />
        </div>
      ) : null}
    </section>
  );
}

/** Where the charity's organization details come from. */
export function SalesforceSummary({ charity }: { readonly charity: Charity }): React.ReactElement {
  if (!charity.salesforceAccountId) {
    return <p className="mt-1 text-portal-small text-portal-muted">Added in bcecloud; not linked to a Salesforce account.</p>;
  }
  const cityState = [charity.address?.city, charity.address?.state].filter(Boolean).join(', ');
  const address = [charity.address?.street, cityState, charity.address?.postalCode].filter(Boolean).join(' · ');
  const synced = charity.syncedAt ? new Date(charity.syncedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  return (
    <div className="mt-1 text-portal-small text-portal-muted">
      <p>
        Name, website, phone and address come from Salesforce{synced ? `; last updated ${synced}` : ''}.
      </p>
      {charity.phone || address ? <p>{[charity.phone, address].filter(Boolean).join(' · ')}</p> : null}
    </div>
  );
}

interface ContactDraft extends CharityContact {
  readonly id: number;
}
let nextContactId = 1;

function CharityForm({ charity }: { readonly charity: Charity | null }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(charity?.name ?? '');
  const [website, setWebsite] = useState(charity?.website ?? '');
  const [ein, setEin] = useState(charity?.ein ?? '');
  const [status, setStatus] = useState<CharityStatus>(charity?.status ?? 'pending');
  const [statusNote, setStatusNote] = useState(charity?.statusNote ?? '');
  const [causeTags, setCauseTags] = useState<readonly string[]>(charity?.causeTags ?? []);
  const [regions, setRegions] = useState<readonly string[]>(charity?.regions.map((region) => [region.city, region.state].filter(Boolean).join(', ')) ?? []);
  const [contacts, setContacts] = useState<readonly ContactDraft[]>(charity?.contacts.map((contact) => ({ ...contact, id: nextContactId++ })) ?? []);

  const save = useMutation({
    mutationFn: () => {
      const body: CharityWrite = {
        name: name.trim(),
        website: website.trim(),
        ein: ein.trim(),
        status,
        statusNote: statusNote.trim(),
        causeTags: [...causeTags],
        regions: regions.map((line) => {
          const [city = '', state = ''] = line.split(',').map((part) => part.trim());
          return { city, state };
        }),
        contacts: contacts.map(({ id: _id, ...contact }) => contact).filter((contact) => contact.name?.trim()),
      };
      return charity ? updateCharity(charity._id, body) : createCharity(body);
    },
    onSuccess: async (saved) => {
      toast.success(charity ? `Saved ${saved.name}` : `Added ${saved.name}`);
      await queryClient.invalidateQueries({ queryKey: ['charities'] });
      await queryClient.invalidateQueries({ queryKey: ['charity', saved._id] });
      navigate('/charities');
    },
  });
  function updateContact(id: number, patch: Partial<CharityContact>): void {
    setContacts((current) => current.map((contact) => (contact.id === id ? { ...contact, ...patch } : contact)));
  }

  return (
    <form
      className="mt-6 space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() && !save.isPending) save.mutate();
      }}
    >
      <Panel className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field guide="charity-name" label="Name" htmlFor="charity-name" required hint={charity?.salesforceAccountId ? 'Kept in step with Salesforce; change it there.' : 'As the charity calls itself.'}>
            <TextInput id="charity-name" value={name} maxLength={160} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field guide="charity-website" label="Website" htmlFor="charity-website" hint={charity?.salesforceAccountId ? 'Kept in step with Salesforce when Salesforce has one.' : 'Their site, for the team and the client-facing documents.'}>
            <TextInput id="charity-website" type="url" value={website} placeholder="https://" onChange={(event) => setWebsite(event.target.value)} />
          </Field>
          <Field guide="charity-ein" label="EIN" htmlFor="charity-ein" hint="The nine-digit tax id, when known; donation receipts need it.">
            <TextInput id="charity-ein" value={ein} maxLength={20} onChange={(event) => setEin(event.target.value)} />
          </Field>
          <Field guide="charity-status" label="Status" htmlFor="charity-status" hint="Approved charities come first in the event picker; do not use hides them.">
            <SelectInput id="charity-status" value={status} onChange={(event) => setStatus(event.target.value as CharityStatus)}>
              {(Object.keys(CHARITY_STATUS_LABELS) as CharityStatus[]).map((key) => (
                <option key={key} value={key}>
                  {CHARITY_STATUS_LABELS[key]}
                </option>
              ))}
            </SelectInput>
          </Field>
        </div>
        <Field guide="charity-status-note" label="Status note" htmlFor="charity-status-note" hint="Why approved, why pending, or why never again.">
          <TextArea id="charity-status-note" rows={2} value={statusNote} maxLength={1000} onChange={(event) => setStatusNote(event.target.value)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <ListField guide="charity-causes" label="Causes, one per line" description="What they work on; the event picker matches these to the program." value={causeTags} onChange={setCauseTags} placeholder={'Veterans\nMusic education'} rows={4} />
          <ListField guide="charity-regions" label="Where they operate, one per line" description="City, State — the event picker ranks nearby charities first." value={regions} onChange={setRegions} placeholder={'Austin, TX\nNashville, TN'} rows={4} />
        </div>
      </Panel>

      <Panel guide="charity-contacts" className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-portal-card-title font-bold">Contacts</h2>
          <Button guide="charity-add-contact" type="button" variant="secondary" size="compact" onClick={() => setContacts((current) => [...current, { id: nextContactId++ }])}>
            <PlusIcon aria-hidden className="size-4" /> Add contact
          </Button>
        </div>
        {contacts.length === 0 ? (
          <EmptyState
            title="No contacts yet"
            message="Who to call at the charity: the rep, the volunteer coordinator, the chapter lead."
            action={
              <Button type="button" variant="secondary" onClick={() => setContacts((current) => [...current, { id: nextContactId++ }])}>
                Add contact
              </Button>
            }
          />
        ) : null}
        {contacts.map((contact) => (
          <div key={contact.id} className="grid gap-3 rounded-portal-button border border-portal-line p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]">
            <Field guide="charity-contact-name" label="Name" htmlFor={`contact-name-${contact.id}`}>
              <TextInput id={`contact-name-${contact.id}`} value={contact.name ?? ''} onChange={(event) => updateContact(contact.id, { name: event.target.value })} />
            </Field>
            <Field guide="charity-contact-role" label="Role" htmlFor={`contact-role-${contact.id}`}>
              <TextInput id={`contact-role-${contact.id}`} value={contact.role ?? ''} placeholder="Rep" onChange={(event) => updateContact(contact.id, { role: event.target.value })} />
            </Field>
            <Field label="Email" htmlFor={`contact-email-${contact.id}`}>
              <TextInput id={`contact-email-${contact.id}`} type="email" value={contact.email ?? ''} onChange={(event) => updateContact(contact.id, { email: event.target.value })} />
            </Field>
            <Field label="Phone" htmlFor={`contact-phone-${contact.id}`}>
              <TextInput id={`contact-phone-${contact.id}`} type="tel" value={contact.phone ?? ''} onChange={(event) => updateContact(contact.id, { phone: event.target.value })} />
            </Field>
            <Field label="Chapter" htmlFor={`contact-chapter-${contact.id}`}>
              <TextInput id={`contact-chapter-${contact.id}`} value={contact.chapter ?? ''} onChange={(event) => updateContact(contact.id, { chapter: event.target.value })} />
            </Field>
            <button
              type="button"
              aria-label="Remove contact"
              onClick={() => setContacts((current) => current.filter((row) => row.id !== contact.id))}
              className="mt-6 grid size-8 place-items-center self-start rounded-md text-portal-muted hover:bg-admin-subtle hover:text-rose-600"
            >
              <XIcon aria-hidden className="size-4" />
            </button>
          </div>
        ))}
      </Panel>

      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => navigate('/charities')} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!name.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : charity ? 'Save charity' : 'Add charity'}
        </Button>
      </div>
    </form>
  );
}
